<template>
  <div class="article-page">
    <ArticlePageSkeleton v-if="loading && !hasArticle" />

    <div v-else-if="!slug || (hasError && !hasArticle)" class="error">
      <p>{{ t('articleDetails.error') }}{{ errorText }}</p>
    </div>

    <div v-else-if="hasArticle && article" class="article-content">
      <div class="article-container">
        <div class="back-button-wrap">
          <UIButton
            variant="secondary-arrow"
            arrow-direction="left"
            :href="backHref"
            :outline="false"
            :text="t('articleDetails.back')"
            padding="18px 34px"
            rounded="8px"
            size="large"
            @click="goBack"
          />
        </div>

        <article class="article-body">
          <!-- Hero image (800×300) згідно макету -->
          <div v-if="article.article_image" class="article-hero-wrap">
            <img
              :src="article.article_image"
              :alt="article.article_title"
              class="article-hero-image"
              width="800"
              height="300"
              loading="eager"
            >
          </div>

          <!-- Мета: дата зліва, перегляди справа -->
          <div class="article-meta">
            <span class="meta-item meta-date">
              <ArticleMetaDateIcon class="meta-icon" />
              {{ formatLocalizedDate(article.article_created_at ?? article.article_modified_at, locale) }}
            </span>
            <span class="meta-item meta-views" :title="t('articleDetails.views')">
              <ArticleViewsIcon class="meta-icon" />
              {{ article.article_views ?? 0 }}
            </span>
          </div>

          <h1 class="article-title">{{ article.article_title }}</h1>

          <VContentRenderer
            v-if="article.article_content?.length"
            :content="article.article_content"
          />

          <!-- Автор статті -->
          <AuthorMeta
            v-if="article.article_author?.name"
            :label="t('articleDetails.author')"
            :name="article.article_author.name"
            :compact="true"
            class="article-author-block"
          />
          <LazyShareButtons
            class="article-share-buttons"
            :label="t('eventDetails.share')"
            :url="shareUrl"
            :text="article.article_title"
          />

          <ArticleSeoKeywordsDev :keywords="article.article_seo_keywords" />

          <!-- CTA: реєстрація на курс -->
          <RegistrationCta class="article-registration-cta"/>
        </article>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useArticleDetails } from '@/composables/article'
import { useSeo } from '@/composables/seo'
import { formatLocalizedDate } from '@/utils/date.helpers'
import { getArticleDescription, toISODate } from '@/utils/articles.helpers'
import ArticleMetaDateIcon from '@/components/pages/article/icons/ArticleMetaDateIcon.vue'
import ArticleViewsIcon from '@/components/pages/article/icons/ArticleViewsIcon.vue'
import AuthorMeta from '@/components/shared/AuthorMeta.vue'

const { t } = useI18n()
const requestUrl = useRequestURL()
definePageMeta({
  layout: 'full-width',
})

const { setupArticleSeo } = useSeo()

const {
  slug,
  article,
  loading,
  error,
  errorText,
  hasArticle,
  hasError,
  backHref,
  goBack,
  loadArticle,
  incrementView,
  locale,
} = useArticleDetails()

// Завантажити статтю на SSR, щоб head (title, description, meta keywords) потрапив у початковий HTML
if (import.meta.server && slug.value) {
  await loadArticle()
}

const shareUrl = computed(() => import.meta.client ? window.location.href : requestUrl.href)

function applyArticleSeo(art: NonNullable<typeof article.value>) {
  const loc = art.article_locale || locale.value
  setupArticleSeo({
    title: { [loc]: art.article_title },
    description: { [loc]: getArticleDescription(art) },
    slug: { [loc]: art.article_slug },
    image: art.article_image || undefined,
    author: art.article_author?.name ?? undefined,
    datePublished: toISODate(art.article_created_at) || '',
    dateModified: toISODate(art.article_modified_at) || '',
    keywords: art.article_seo_keywords?.length ? art.article_seo_keywords : undefined,
  })
}

watch(
  () => [article.value, hasArticle.value] as const,
  ([art, has]) => {
    if (!has || !art) return
    applyArticleSeo(art)
  },
  { immediate: true },
)

async function loadAndSyncViews() {
  await loadArticle()
  await incrementView()
  await loadArticle()
}

onMounted(() => {
  loadAndSyncViews()
})

watch(slug, (newSlug, oldSlug) => {
  if (newSlug && newSlug !== oldSlug) {
    loadAndSyncViews()
  }
})
</script>

<style scoped>
.article-page {
  max-width: var(--max-width-container);
  margin: 0 auto;
  padding: 0 40px;
}

.article-content {
  max-width: var(--max-width-container);
  margin: 0 auto;
}

.article-container {
  max-width: var(--max-width-container);
  margin: 0 auto;
  padding: 0 40px;
  margin-top: 50px;
}

.back-button-wrap {
  margin-bottom: 2rem;
}

.article-body {
  max-width: 800px;
  margin: 0 auto;
}

/* Hero 800×300 згідно макету */
.article-hero-wrap {
  width: 100%;
  aspect-ratio: 800 / 300;
  margin-bottom: 24px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-grey-light, #e5e7eb);
}

.article-hero-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

/* Мета: дата зліва, перегляди справа */
.article-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-family: var(--font-inter);
  font-size: 14px;
  color: var(--color-grey);
  margin-bottom: 20px;
}

.article-registration-cta {
  margin-bottom: 128px;
}

.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.meta-icon {
  flex-shrink: 0;
  color: var(--color-grey);
}

.meta-date .meta-icon {
  color: var(--color-primary);
}

.article-title {
  font-family: var(--font-inter);
  font-weight: 600;
  font-size: 42px;
  line-height: 1.2;
  color: var(--color-dark-blue);
  margin: 0 0 24px 0;
}

.article-author-block {
  margin-top: 24px;
}

.article-share-buttons {
  margin-top: 16px;
}

.loading,
.error {
  min-height: 55vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 60px 20px;
  font-family: var(--font-inter);
}

.error {
  color: #dc3545;
}

@media (max-width: 991px) {
  .article-container {
    padding: 0 30px;
  }

  .article-title {
    font-size: 32px;
  }
}

@media (max-width: 767px) {
  .article-container {
    padding: 0 20px;
  }

  .article-title {
    font-size: 28px;
  }
}
</style>
